"use client"

import { useState } from "react"
import { Calculator, DollarSign, AlertTriangle, ShieldAlert, TrendingUp, Info } from "lucide-react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Header } from "@/components/header"
import { Footer } from "@/components/footer"

interface PenaltyTier {
  id: string
  name: string
  description: string
  minPerViolation: number
  maxPerViolation: number
  annualMax: number
  examples: string[]
}

const penaltyTiers: PenaltyTier[] = [
  {
    id: "tier1",
    name: "Tier 1: Unknowing Violation",
    description: "Individual did not know (and by exercising reasonable diligence would not have known) that they violated HIPAA",
    minPerViolation: 100,
    maxPerViolation: 50000,
    annualMax: 25000,
    examples: [
      "Minor security gap discovered during routine audit",
      "Unintentional disclosure due to technical glitch",
      "First-time administrative oversight with no harm",
    ],
  },
  {
    id: "tier2",
    name: "Tier 2: Reasonable Cause",
    description: "Violation due to reasonable cause and not willful neglect",
    minPerViolation: 1000,
    maxPerViolation: 50000,
    annualMax: 100000,
    examples: [
      "Failure to encrypt data despite security policy",
      "Inadequate workforce training leading to breach",
      "Delayed breach notification (but not intentional)",
    ],
  },
  {
    id: "tier3",
    name: "Tier 3: Willful Neglect (Corrected)",
    description: "Violation due to willful neglect but corrected within 30 days",
    minPerViolation: 10000,
    maxPerViolation: 50000,
    annualMax: 250000,
    examples: [
      "Known security gap left unaddressed, then fixed",
      "Ignoring required risk assessment, later completed",
      "Delayed BAA implementation but eventually signed",
    ],
  },
  {
    id: "tier4",
    name: "Tier 4: Willful Neglect (Not Corrected)",
    description: "Violation due to willful neglect and not corrected within 30 days",
    minPerViolation: 50000,
    maxPerViolation: 50000,
    annualMax: 1500000,
    examples: [
      "Persistent failure to encrypt ePHI after warnings",
      "Refusing to implement required safeguards",
      "Ongoing violations despite HHS investigation",
    ],
  },
]

export default function PenaltyCalculatorContent() {
  const [selectedTier, setSelectedTier] = useState<string>("tier1")
  const [violationCount, setViolationCount] = useState<number>(1)
  const [individualsPenalty, setIndividualsPenalty] = useState<number>(0)
  const [showResults, setShowResults] = useState<boolean>(false)

  const currentTier = penaltyTiers.find(t => t.id === selectedTier)!

  const calculatePenalties = () => {
    // Civil penalties from HHS/OCR
    const perViolationAvg = (currentTier.minPerViolation + currentTier.maxPerViolation) / 2
    const totalCivil = Math.min(perViolationAvg * violationCount, currentTier.annualMax)

    // If many individuals affected, add potential class action/state AG penalties
    const estimatedIndividualPenalty = individualsPenalty > 0 ? individualsPenalty * 500 : 0 // Average settlement per affected individual

    return {
      civilMin: Math.min(currentTier.minPerViolation * violationCount, currentTier.annualMax),
      civilMax: Math.min(currentTier.maxPerViolation * violationCount, currentTier.annualMax),
      civilAvg: totalCivil,
      individualSettlement: estimatedIndividualPenalty,
      total: totalCivil + estimatedIndividualPenalty,
    }
  }

  const results = showResults ? calculatePenalties() : null

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount)
  }

  const getRiskLevel = (amount: number): { level: string; color: string; bgColor: string } => {
    if (amount < 50000) return { level: "Low", color: "text-yellow-700", bgColor: "bg-yellow-100" }
    if (amount < 250000) return { level: "Moderate", color: "text-orange-700", bgColor: "bg-orange-100" }
    if (amount < 1000000) return { level: "High", color: "text-red-700", bgColor: "bg-red-100" }
    return { level: "Critical", color: "text-red-900", bgColor: "bg-red-200" }
  }

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#0EA5E9] to-[#0284C7] text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <Badge className="bg-white/20 text-white hover:bg-white/30 mb-6">
              <Calculator className="mr-2 h-4 w-4" />
              Interactive Tool
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">HIPAA Penalty Calculator</h1>
            <p className="text-xl text-white/90">
              Estimate potential financial penalties for HIPAA violations based on HHS Office for Civil Rights penalty tiers. Understand the financial risk of non-compliance.
            </p>
          </div>
        </div>
      </section>

      {/* Calculator Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Input Panel */}
              <div>
                <Card className="border-2 border-[#0EA5E9] mb-6">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Calculator className="h-6 w-6 text-[#0EA5E9]" />
                      Calculate Potential Penalties
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-6">
                      {/* Tier Selection */}
                      <div>
                        <label className="block text-sm font-bold text-[#0F172A] mb-3">
                          Select Violation Tier
                        </label>
                        <div className="space-y-3">
                          {penaltyTiers.map((tier) => (
                            <div
                              key={tier.id}
                              className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                                selectedTier === tier.id
                                  ? "border-[#0EA5E9] bg-[#F0F9FF]"
                                  : "border-[#E2E8F0] hover:border-[#0EA5E9]"
                              }`}
                              onClick={() => setSelectedTier(tier.id)}
                            >
                              <div className="flex items-start gap-3">
                                <input
                                  type="radio"
                                  name="tier"
                                  checked={selectedTier === tier.id}
                                  onChange={() => setSelectedTier(tier.id)}
                                  className="mt-1"
                                />
                                <div className="flex-1">
                                  <h4 className="font-bold text-[#0F172A] mb-1">{tier.name}</h4>
                                  <p className="text-sm text-[#64748B] mb-2">{tier.description}</p>
                                  <p className="text-xs text-[#0369A1] font-medium">
                                    {formatCurrency(tier.minPerViolation)} - {formatCurrency(tier.maxPerViolation)} per violation
                                  </p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Number of Violations */}
                      <div>
                        <label className="block text-sm font-bold text-[#0F172A] mb-2">
                          Number of Violations
                        </label>
                        <p className="text-xs text-[#64748B] mb-3">
                          Each identical violation occurring on separate days counts as a separate violation
                        </p>
                        <input
                          type="number"
                          min="1"
                          max="365"
                          value={violationCount}
                          onChange={(e) => setViolationCount(Math.max(1, parseInt(e.target.value) || 1))}
                          className="w-full px-4 py-3 border-2 border-[#E2E8F0] rounded-lg focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent text-lg font-bold"
                        />
                        <p className="text-xs text-[#64748B] mt-2">
                          Maximum penalties are capped at {formatCurrency(currentTier.annualMax)} per calendar year for identical violations
                        </p>
                      </div>

                      {/* Individuals Affected */}
                      <div>
                        <label className="block text-sm font-bold text-[#0F172A] mb-2">
                          Number of Individuals Affected (Optional)
                        </label>
                        <p className="text-xs text-[#64748B] mb-3">
                          For breach scenarios, estimate potential class action or state attorney general penalties
                        </p>
                        <input
                          type="number"
                          min="0"
                          value={individualsPenalty}
                          onChange={(e) => setIndividualsPenalty(Math.max(0, parseInt(e.target.value) || 0))}
                          className="w-full px-4 py-3 border-2 border-[#E2E8F0] rounded-lg focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent text-lg font-bold"
                          placeholder="0"
                        />
                      </div>

                      <Button
                        onClick={() => setShowResults(true)}
                        size="lg"
                        className="w-full bg-[#0EA5E9] hover:bg-[#0284C7] text-white"
                      >
                        <DollarSign className="mr-2 h-5 w-5" />
                        Calculate Penalties
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Examples */}
                {currentTier && (
                  <Card className="border border-[#E2E8F0]">
                    <CardHeader>
                      <CardTitle className="text-base flex items-center gap-2">
                        <Info className="h-5 w-5 text-[#0EA5E9]" />
                        Examples of {currentTier.name}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2">
                        {currentTier.examples.map((example, index) => (
                          <li key={index} className="flex items-start gap-2 text-sm text-[#64748B]">
                            <span className="text-[#0EA5E9] mt-1">•</span>
                            <span>{example}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                )}
              </div>

              {/* Results Panel */}
              <div>
                {showResults && results ? (
                  <div className="space-y-6">
                    {/* Total Estimated Penalty */}
                    <Card className="border-2 border-[#DC2626] bg-gradient-to-br from-[#FEF2F2] to-white">
                      <CardContent className="pt-6">
                        <div className="text-center mb-6">
                          <h3 className="text-sm font-medium text-[#64748B] mb-2">Estimated Total Penalty</h3>
                          <div className="text-5xl font-bold text-[#DC2626] mb-2">
                            {formatCurrency(results.total)}
                          </div>
                          <Badge className={`${getRiskLevel(results.total).bgColor} ${getRiskLevel(results.total).color}`}>
                            {getRiskLevel(results.total).level} Risk
                          </Badge>
                        </div>

                        {/* Breakdown */}
                        <div className="space-y-4 pt-6 border-t border-[#E2E8F0]">
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-[#64748B]">HHS Civil Penalties Range:</span>
                            <span className="font-bold text-[#0F172A]">
                              {formatCurrency(results.civilMin)} - {formatCurrency(results.civilMax)}
                            </span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-[#64748B]">Average Civil Penalty:</span>
                            <span className="font-bold text-[#0F172A]">{formatCurrency(results.civilAvg)}</span>
                          </div>
                          {individualsPenalty > 0 && (
                            <div className="flex justify-between items-center">
                              <span className="text-sm text-[#64748B]">Est. Individual Settlements:</span>
                              <span className="font-bold text-[#0F172A]">{formatCurrency(results.individualSettlement)}</span>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>

                    {/* Additional Costs Warning */}
                    <Card className="border-2 border-[#F59E0B] bg-[#FEF3C7]">
                      <CardContent className="pt-6">
                        <div className="flex items-start gap-3">
                          <AlertTriangle className="h-6 w-6 text-[#F59E0B] flex-shrink-0 mt-0.5" />
                          <div>
                            <h4 className="font-bold text-[#92400E] mb-2">Additional Costs Not Included</h4>
                            <ul className="space-y-1 text-sm text-[#92400E]">
                              <li>• Breach notification costs (mailings, call centers)</li>
                              <li>• Credit monitoring for affected individuals</li>
                              <li>• Legal and forensic investigation fees</li>
                              <li>• Potential criminal penalties (up to $250,000)</li>
                              <li>• Reputation damage and lost business</li>
                              <li>• Corrective action plan implementation</li>
                              <li>• State attorney general fines</li>
                            </ul>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Recent Settlement Examples */}
                    <Card className="border border-[#E2E8F0]">
                      <CardHeader>
                        <CardTitle className="text-base flex items-center gap-2">
                          <TrendingUp className="h-5 w-5 text-[#0EA5E9]" />
                          Recent HIPAA Settlements
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-4">
                          <div className="pb-3 border-b border-[#E2E8F0]">
                            <div className="flex justify-between items-start mb-1">
                              <span className="font-bold text-[#0F172A]">Premera Blue Cross (2020)</span>
                              <span className="text-[#DC2626] font-bold">$6.85M</span>
                            </div>
                            <p className="text-xs text-[#64748B]">Breach affecting 10.4 million individuals due to insufficient risk analysis and security measures</p>
                          </div>
                          <div className="pb-3 border-b border-[#E2E8F0]">
                            <div className="flex justify-between items-start mb-1">
                              <span className="font-bold text-[#0F172A]">Anthem (2018)</span>
                              <span className="text-[#DC2626] font-bold">$16M</span>
                            </div>
                            <p className="text-xs text-[#64748B]">Largest healthcare data breach in history affecting 79 million individuals, lack of encryption</p>
                          </div>
                          <div className="pb-3 border-b border-[#E2E8F0]">
                            <div className="flex justify-between items-start mb-1">
                              <span className="font-bold text-[#0F172A]">Cignet Health (2011)</span>
                              <span className="text-[#DC2626] font-bold">$4.3M</span>
                            </div>
                            <p className="text-xs text-[#64748B]">Failure to provide patients access to medical records and cooperate with OCR investigation</p>
                          </div>
                          <div>
                            <div className="flex justify-between items-start mb-1">
                              <span className="font-bold text-[#0F172A]">Banner Health (2020)</span>
                              <span className="text-[#DC2626] font-bold">$1.25M</span>
                            </div>
                            <p className="text-xs text-[#64748B]">Breach affecting 2.81 million individuals, failure to conduct enterprise-wide risk analysis</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                ) : (
                  <Card className="border-2 border-[#E2E8F0] h-full flex items-center justify-center">
                    <CardContent className="text-center py-16">
                      <ShieldAlert className="h-16 w-16 text-[#94A3B8] mx-auto mb-4" />
                      <h3 className="text-xl font-bold text-[#0F172A] mb-2">Calculate Your Risk</h3>
                      <p className="text-[#64748B]">
                        Select a violation tier and enter the details to see estimated penalties
                      </p>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Information Section */}
      <section className="py-16 bg-[#F8FAFC]">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-[#0F172A] mb-8 text-center">Understanding HIPAA Penalty Tiers</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="border border-[#E2E8F0]">
                <CardContent className="pt-6">
                  <h3 className="font-bold text-[#0F172A] mb-3">How Penalties Are Determined</h3>
                  <ul className="space-y-2 text-sm text-[#64748B]">
                    <li>• Level of negligence (unknowing to willful neglect)</li>
                    <li>• Whether violation was corrected within 30 days</li>
                    <li>• Number of individuals affected</li>
                    <li>• Duration of violation</li>
                    <li>• Financial condition of the entity</li>
                    <li>• History of prior compliance</li>
                  </ul>
                </CardContent>
              </Card>
              <Card className="border border-[#E2E8F0]">
                <CardContent className="pt-6">
                  <h3 className="font-bold text-[#0F172A] mb-3">Criminal Penalties</h3>
                  <ul className="space-y-2 text-sm text-[#64748B]">
                    <li>• Tier 1: Up to $50,000 fine and 1 year in prison</li>
                    <li>• Tier 2 (false pretenses): Up to $100,000 and 5 years</li>
                    <li>• Tier 3 (intent to sell/transfer): Up to $250,000 and 10 years</li>
                    <li>• Applies to individuals who knowingly violate HIPAA</li>
                    <li>• Prosecuted by Department of Justice</li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-[#0EA5E9] to-[#0284C7]">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-6">Avoid These Penalties</h2>
            <p className="text-xl text-white/90 mb-8">
              The best way to avoid HIPAA penalties is proactive compliance. Our experts can help you identify gaps and implement the right safeguards before violations occur.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="/self-assessment">
                <Button size="lg" className="bg-white text-[#0EA5E9] hover:bg-white/90">
                  Free Compliance Assessment
                </Button>
              </a>
              <a href="/contact">
                <Button size="lg" className="bg-white/20 text-white border-2 border-white hover:bg-white hover:text-[#0EA5E9]">
                  Talk to an Expert
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  )
}
